import React, {useState} from 'react';
import {Appbar, Button, Menu, Tooltip} from 'react-native-paper';
import {useAppDispatch, useAppSelector} from '../../../store/hooks';
import {startOnboarding} from '../../../store/slices/upload';
import {FolderType} from '../../../types';
import {
  FolderParams,
  ProjectParams,
  ShopStackParams,
  UnifiedStackParams,
} from '../../../types/nav';
import EditorToolBar from '../../editor/editortoolbar';
import {setEditingMode} from '../../../store/slices/ui';
import {
  NavigationProp,
  useNavigation,
} from '@react-navigation/native';
import {useQuery} from 'react-query';
import {Platform} from 'react-native';
import {networks} from '../../../api/exchange';
import {switchNet} from '../../../api/metamask';
import {AttachmentDrawer} from '../../contacts/components/attachmentdrawer';
import {useFileSelectOptions} from '../../../hooks/useFileSelectOptions';
import {useKeyboardDispatcher} from '../../../hooks/useKeyboardListener';
export function Actions({
  showFullSearchBar,
}: {
  showFullSearchBar: boolean;
}) {
  useKeyboardDispatcher();
  const navigation =
    useNavigation<NavigationProp<UnifiedStackParams>>();
  const dispatch = useAppDispatch();
  const params = useAppSelector(state => state.ui.params);
  const folderType = useAppSelector(state => state.ui.folderType);
  const routeName = useAppSelector(state => state.ui.currentPage);
  const parentRouteName = useAppSelector(
    state => state.ui.parentName,
  );
  const projectId = (params as ProjectParams)?.projectId;
  const folderId = (params as FolderParams)?.folderId;
  const shopId = (params as ShopStackParams['Orders'])?.shopId;
  const isShop =
    parentRouteName === 'ShopTab' && typeof shopId === 'string';
  const isEditing =
    useAppSelector(state => state.ui.editingMode) !== undefined;

  let addFileEnabled = [
    FolderType.media,
    FolderType.editor,
    FolderType.encoder,
    FolderType.shop,
  ].includes(folderType);

  let addFolderEnabled = folderType !== FolderType.settings;
  const showScaleControls = routeName === 'EditorProject';

  if (showScaleControls) {
    addFileEnabled = false;
    addFolderEnabled = false;
  }

  const onClickFolder = () => {
    dispatch(setEditingMode('folder'));
  };

  const onClickSearch = () => {
    dispatch(setEditingMode('search'));
  };

  const onClickContact = () => {
    dispatch(setEditingMode('party'));
  };

  const onClickDatabase = () => {
    dispatch(setEditingMode('database'));
  };
  const networkQuery = useQuery(
    'network',
    () => {
      try {
        if (Platform.OS === 'web' && window.ethereum) {
          return window.ethereum.networkVersion;
        }
      } catch (err) {
        return undefined;
      }
      return undefined;
    },
    // {refetchInterval: 10},
  );
  const [networksShown, showNetworks] = useState(false);
  const fileSelectOptions = useFileSelectOptions(
    folderId,
    undefined,
    true,
  );
  return (
    <>
      {networkQuery.data && (
        <Tooltip title="Switch Network">
          <Menu
            visible={networksShown}
            onDismiss={() => showNetworks(false)}
            contentStyle={{marginTop: 50}}
            anchor={
              <Button
                icon={'ethereum'}
                onPress={() => showNetworks(true)}>
                {
                  networks[networkQuery.data as keyof typeof networks]
                    .chainName
                }
              </Button>
            }>
            {Object.keys(networks).map(
              (networkId: keyof typeof networks) => {
                const n = networks[networkId];
                return (
                  <Menu.Item
                    key={n.chainId}
                    title={n.chainName}
                    onPress={async () => {
                      await switchNet(n.chainId);
                      showNetworks(false);
                    }}
                  />
                );
              },
            )}
          </Menu>
        </Tooltip>
      )}
      {routeName === 'Databases' ? (
        <Appbar.Action
          icon={'database-plus'}
          onPress={onClickDatabase}
        />
      ) : null}
      {addFolderEnabled && !isEditing ? (
        <Tooltip title="Create a folder">
          <Appbar.Action
            icon="folder-plus"
            onPress={() => onClickFolder()}
          />
        </Tooltip>
      ) : null}
      {showScaleControls && (
        <EditorToolBar projectId={projectId} routeParams={params} />
      )}
      {isShop && (
        <Tooltip title="Open webeditor">
          <Appbar.Action
            icon="web"
            onPress={() => navigation.navigate('WebEditor')}
          />
        </Tooltip>
      )}
      {isShop && (
        <Tooltip title="Open shop info">
          <Appbar.Action
            icon="cog"
            onPress={() => navigation.navigate('ShopConfig')}
          />
        </Tooltip>
      )}
      {isShop && (
        <Tooltip title="Open orders">
          <Appbar.Action
            icon="shopping"
            onPress={() =>
              navigation.navigate('Orders', {orderId: undefined})
            }
          />
        </Tooltip>
      )}
      {isShop && (
        <Tooltip title="Open payouts">
          <Appbar.Action
            icon="currency-usd"
            onPress={() => navigation.navigate('Payouts', {shopId})}
          />
        </Tooltip>
      )}
      {!isEditing && folderType === 'contact' ? (
        <Tooltip title="Create a chat">
          <Appbar.Action
            icon="chat-plus"
            onPress={() => onClickContact()}
          />
        </Tooltip>
      ) : null}
      {addFileEnabled && !isEditing ? (
        <Tooltip title="Select a file">
          <Appbar.Action
            icon="file-plus"
            onPress={fileSelectOptions.showDrawer}
          />
        </Tooltip>
      ) : null}
      {!showFullSearchBar && !isEditing ? (
        <Appbar.Action
          icon="magnify"
          onPress={() => onClickSearch()}
        />
      ) : null}
      <AttachmentDrawer {...fileSelectOptions} />
    </>
  );
}
